import styled from "styled-components";
import { tokens } from "../../../../public/tokens";
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from "../../../library/breakpoints";
import Button from "../../../library/Button";

const cardSurface = {
    background: tokens.colors.surfaces["container-background"],
    borderWidth: tokens.globals["spacing-px"],
    borderStyle: "solid",
    borderColor: tokens.colors.surfaces["border-divider"],
    borderRadius: tokens.globals["border-radius"],
};

const sectionHeading = {
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    fontSize: tokens.typography.fontSize["base-16px"],
    color: tokens.colors.text.primary,
};

/**
 * SD-3 draws every table cell as a fixed 56px-tall row with all padding on the horizontal axis,
 * so vertical padding here would stack on top of that height instead of forming it.
 */
const dataCell = {
    height: "56px",
    padding: `0 ${tokens.globals["spacing-2"]}`,
    textAlign: "left",
    borderStyle: "none",
    borderBottomWidth: tokens.globals["spacing-px"],
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colors.surfaces["border-divider"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.regular,
    color: tokens.colors.text.primary,
    verticalAlign: "middle",
    wordBreak: "break-word",

    "&:first-child": {
        paddingLeft: tokens.globals["spacing-4"],
    },
    "&:last-child": {
        paddingRight: tokens.globals["spacing-4"],
    },
};

export const StyledToolbarRow = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-4"],
});

export const StyledLockedBannerWrap = styled.div({
    marginTop: tokens.globals["spacing-4"],
    marginBottom: tokens.globals["spacing-4"],
});

export const StyledToolbarActions = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
});

export const StyledTwoColumnLayout = styled.div({
    display: "grid",
    gridTemplateColumns: "minmax(420px, 35%) 1fr",
    gap: tokens.globals["spacing-5"],
    alignItems: "flex-start",

    [`@media (max-width: ${TABLET_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
});

export const StyledPhaseColumn = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-2"],
});

export const StyledMainColumn = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-5"],
});

export const StyledPhaseCard = styled.div(({ $selected = false }) => ({
    ...cardSurface,
    padding: tokens.globals["spacing-3"],
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.globals["spacing-2-5"],
    cursor: "pointer",
    borderColor: $selected ? tokens.colors.brand.blue["500"] : tokens.colors.surfaces["border-divider"],
    boxShadow: $selected ? tokens.effects.effects.shadow.hover : "none",
}));

export const StyledPhaseCardContent = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-1-5"],
    minWidth: 0,
    flex: 1,
});

export const StyledPhaseName = styled.span({
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    fontSize: tokens.typography.fontSize["lg-14px"],
    lineHeight: tokens.typography.lineHeights["100"],
    color: tokens.colors.text.primary,
    wordBreak: "break-word",
});

export const StyledBadgeRow = styled.div({
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-2"],
});

export const StyledRearrangeRow = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-3"],
    width: "100%",

    "> *": {
        flex: 1,
        minWidth: 0,
    },
});

export const StyledRearrangeRowContent = styled.div({
    ...cardSurface,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-3"],
    padding: `${tokens.globals["spacing-2-5"]} ${tokens.globals["spacing-4"]}`,
    width: "100%",
});

export const StyledRearrangeRowMain = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-3"],
    minWidth: 0,
});

export const StyledDragHandle = styled.i({
    flexShrink: 0,
    fontSize: tokens.typography.fontSize["lg-14px"],
    color: tokens.colors.text.secondary,
    cursor: "grab",
});

export const StyledRearrangeTree = styled.div({
    ".rstcustom__rowContents": {
        background: "transparent",
        border: "none",
        boxShadow: "none",
        padding: 0,
        fontWeight: "normal",
    },
    ".rstcustom__rowContents:hover": {
        background: "transparent !important",
        boxShadow: "none",
    },
    ".rstcustom__rowWrapper, .rstcustom__rowWrapper:hover": {
        padding: `${tokens.globals["spacing-0-5"]} ${tokens.globals["spacing-0-5"]} ${tokens.globals["spacing-0-5"]} 0`,
    },
    ".rstcustom__rowLabel": {
        paddingRight: 0,
    },
    ".rstcustom__nodeContent": {
        width: "100%",
        position: "static",
    },
    ".rstcustom__highlight": {
        background: "none",
    },
    ".rstcustom__rowLandingPad, .rstcustom__rowCancelPad": {
        width: "100%",
    },
    ".rstcustom__rowLandingPad::before, .rstcustom__rowCancelPad::before": {
        top: tokens.globals["spacing-1"],
        bottom: tokens.globals["spacing-1"],
        borderRadius: tokens.globals["border-radius"],
    },
});

export const StyledSectionHeading = styled.div(sectionHeading);

export const StyledCreditsInputWrap = styled.div({
    width: "205px",
});

export const StyledTableWrapper = styled.div({
    width: "100%",
    overflowX: "auto",
});

/**
 * Legacy made every module name a link to the module (StudyPath.js:223). The spec only forbids
 * opening a row from its *non-action* area, which a name link isn't.
 */
export const StyledModuleLink = styled.a({
    color: tokens.colors.text.link,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    textDecoration: "none",
    cursor: "pointer",

    "&:hover": {
        textDecoration: "underline",
    },
});

export const StyledRowLabel = styled.span(({ $muted = false }) => ({
    color: $muted ? tokens.colors.text.secondary : tokens.colors.text.primary,
}));

export const StyledWarningDot = styled.i({
    color: tokens.colors.states.warning.default,
    marginLeft: tokens.globals["spacing-1-5"],
});

export const StyledEmptyMessage = styled.div({
    padding: tokens.globals["spacing-5"],
    textAlign: "center",
    color: tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
});

export const StyledLoadingRow = styled.div({
    padding: tokens.globals["spacing-5"],
    textAlign: "center",
    color: tokens.colors.text.secondary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
});

// Clears the fixed StickyFooter so it never covers the last row of the Unassigned panel (SPEC3 R4).
export const StyledPageStack = styled.div({
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-6"],
    paddingBottom: tokens.globals["spacing-24"],
});

export const StyledFormCard = styled.div({
    ...cardSurface,
    padding: tokens.globals["spacing-6"],
    display: "flex",
    flexDirection: "column",
    gap: tokens.globals["spacing-3"],
});

// SD-3's four-column grid leaves the fourth column empty so the three fields keep their width instead of stretching.
export const StyledFormFieldsRow = styled.div({
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: tokens.globals["spacing-5"],

    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
        gridTemplateColumns: "1fr",
    },
});

export const StyledTableCard = styled.div({
    ...cardSurface,
    overflow: "hidden",
});

/**
 * Only the table's very last row, not the last row of each group's own tbody — the divider
 * between one group's last module and the next group's band has to stay.
 */
export const StyledDataTable = styled.table({
    width: "100%",
    minWidth: "720px",
    tableLayout: "fixed",
    border: "none",
    borderCollapse: "collapse",
    
    "tbody:last-of-type tr:last-child > td": {
        borderBottom: "none",
    },
});

export const StyledDataTh = styled.th(({ $width, $sortable = false }) => ({
    ...dataCell,
    width: $width,
    whiteSpace: "nowrap",
    cursor: $sortable ? "pointer" : "default",
    userSelect: "none",
}));


/**
 * SD-3's column dividers only appear between data rows, not across the header row, so the
 * right border lives here rather than in the shared `dataCell` base StyledDataTh also uses.
 */
export const StyledDataTd = styled.td({
    ...dataCell,
    "&:not(:last-child)": {
        borderRightWidth: tokens.globals["spacing-px"],
        borderRightStyle: "solid",
        borderRightColor: tokens.colors.surfaces["border-divider"],
    },
});

// SD-3 dims (not hides) the sort icon on inactive headers, keeping the affordance visible on every column.
export const StyledDataSortIcon = styled.i(({ $active = false }) => ({
    marginLeft: tokens.globals["spacing-2-5"],
    fontSize: tokens.typography.fontSize["md-12px"],
    color: $active ? tokens.colors.text.primary : tokens.colors.text.disabled,
}));

export const StyledTableMessageCell = styled.td({
    ...dataCell,
    textAlign: "center",
    color: tokens.colors.text.secondary,
});

/**
 * The band spans every column, so it carries the row's own height and padding rather than
 * inheriting the 56px data-cell geometry.
 */
export const StyledGroupBandCell = styled.td({
    padding: `${tokens.globals["spacing-3-5"]} ${tokens.globals["spacing-2"]} ${tokens.globals["spacing-3-5"]} ${tokens.globals["spacing-4"]}`,
    background: tokens.colors.neutral.grey["100"],
    borderStyle: "none",
    borderBottomWidth: tokens.globals["spacing-px"],
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colors.surfaces["border-divider"],
});

/**
 * The Unassigned panel's title sits in the same band as a module group's, but above its table
 * rather than inside it, so it needs the padding a `<td>` would otherwise provide.
 */
export const StyledPanelBandHeader = styled.div({
    padding: `${tokens.globals["spacing-3-5"]} ${tokens.globals["spacing-2"]} ${tokens.globals["spacing-3-5"]} ${tokens.globals["spacing-4"]}`,
    borderBottomWidth: tokens.globals["spacing-px"],
    borderBottomStyle: "solid",
    borderBottomColor: tokens.colors.surfaces["border-divider"],
});

export const StyledCardBody = styled.div({
    padding: tokens.globals["spacing-4"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    color: tokens.colors.text.secondary,
});

export const StyledGroupBandRow = styled.div({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-4"],
    minHeight: tokens.globals["spacing-10"],
});

export const StyledGroupBandLeft = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-2"],
});

export const StyledGroupBandControls = styled.div({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.globals["spacing-2"],
});

/**
 * SD-3 puts 16px between this label and the credits input, where the band's own flex gap is 8.
 */
export const StyledMaxCreditsLabel = styled.span({
    marginRight: tokens.globals["spacing-2"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    color: tokens.colors.text.primary,
    whiteSpace: "nowrap",
});

export const StyledGroupName = styled.span({
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
    fontSize: tokens.typography.fontSize["lg-14px"],
    color: tokens.colors.text.primary,
});

export const StyledRowActions = styled.div({
    display: "flex",
    alignItems: "center",
    gap: tokens.globals["spacing-2-5"],
});

/**
 * SD-3's action button is a plain bordered white control, which `Button`'s own variants don't
 * cover — the closest, `secondary`, fills with brand blue. The label colour can't be set here:
 * `Button` re-declares it `!important` for every interaction state, so callers pass
 * `colorOverride`, which feeds those rules instead of losing to them.
 */
export const StyledBandButton = styled(Button)({
    height: tokens.globals["spacing-10"],
    background: tokens.colors.surfaces["container-background"],
    outline: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    outlineOffset: `-${tokens.globals["spacing-px"]}`,
    whiteSpace: "nowrap",

    "&:hover:not(:disabled)": {
        background: tokens.colors.neutral.grey["100"],
    },
});
